<template>
  <div class="twilio-call-wrapper dark" >
    <button 
      href="#" 
      class="call-link twilio-call-link"
      @click.prevent="showForm = true"
    >
      {{ labelsState['request_call'] || 'Call Me' }}
      <i class="fa-solid fa-angle-right"></i>
    </button>

    <div class="modal-overlay" v-if="showForm" @click.self="showForm = false">
      <div class="modal-container">
        <h3 class="modal-title">{{ labelsState['request_call_title'] || 'Request a Call' }}</h3>
        
        <form @submit.prevent="submitForm">
          <div class="form-group">
            <label>{{ labelsState['name'] || 'Name' }}</label>
            <input 
              type="text" 
              v-model="formData.name" 
              :placeholder="labelsState['name'] || 'Name'"
            >
          </div>
          
          <div class="form-group">
            <label>{{ labelsState['email'] || 'Email' }}</label>
            <input 
              type="email" 
              v-model="formData.email" 
              :placeholder="labelsState['your_email'] || 'your@email.com'"
            >
          </div>
          
          <div class="form-group">
            <label>{{ labelsState['phone_required'] || 'Phone (required)' }}</label>
            <div class="phone-input-container">
                <select
                v-model="countryCode"
                class="country-code-select"
                >
                <option v-for="country in countries" 
                        :key="country.phoneCode" 
                        :value="country.phoneCode">
                    {{ country.phoneCode }} {{ country.countryCode }}
                </option>
                </select>
                
                <input
                v-model="formData.phone"
                type="text"
                class="phone-number-input"
                placeholder="302 123 4567"
                @input="formData.phone = formData.phone.replace(/\D/g, '')"
                required
                >
            </div>
            </div>
          
          <div class="button-group">
            <a 
                 href="#" 
                 class="cancel-button" 
                 @click.prevent="!isSubmitting && (showForm = false)"
                 :class="{ 'disabled': isSubmitting }"
               >
              {{ labelsState['cancel'] || 'Cancel' }}
            </a>
            <a 
                 href="#" 
                 class="submit-button" 
                 @click.prevent="!isSubmitting && submitForm()"
                 :class="{ 'disabled': isSubmitting }"
               >
              {{ labelsState['submit_request'] || 'Request Call' }}
              <i class="fa-solid fa-angle-right"></i>
            </a>
          </div>
        </form>
        
        <div 
          v-if="statusMessage" 
          class="status-message" 
          :class="{ 'error': hasError, 'success': !hasError }"
        >
          {{ statusMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, computed, onMounted } from 'vue';
import { useLabelsStore } from '@/stores/labels';
import "../../../assets/css/global.css";
export default {
  name: 'TwilioCallButton',
  props: {
    configId: {
      type: String,
      required: true
    },
    apiHost: {
      type: String,
      default: `${process.env.VUE_APP_HOST_ROLESANDADMINISTRATION}`
    }
  },
  setup(props) {
    const labelsStore = useLabelsStore();
    const labelsState = computed(() => labelsStore.labels);
    
    const showForm = ref(false);
    const countries = ref([]);
    const countryCode = ref('+57');
    const formData = ref({
      name: '',
      email: '',
      phone: ''
    });
    const statusMessage = ref('');
    const isSubmitting = ref(false);
    const hasError = ref(false);
    
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${process.env.VUE_APP_HOST_ROLESANDADMINISTRATION}/countries`);
        countries.value = response.data.countries;
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    
    onMounted(() => {
      fetchCountries();
    });
    
    const submitForm = async () => {
      if (!formData.value.phone.trim()) {
        statusMessage.value = labelsState.value['phone_required_error'] || 'Please enter your phone number.';
        hasError.value = true;
        return;
      }
      if (!formData.value.name.trim()) {
        statusMessage.value = labelsState.value['name_required_error'] || 'Please enter your name.';
        hasError.value = true;
        return;
      }
      isSubmitting.value = true;
      statusMessage.value = labelsState.value['processing_request'] || 'Processing your request...';
      hasError.value = false;
      
      try {
        const clientData = {
          name: formData.value.name.trim(),
          email: formData.value.email.trim(),
          phone: `${countryCode.value}${formData.value.phone.trim()}`,
          config_id: props.configId
        };
        
        const response = await axios.post(
          `${props.apiHost}/twilio/request-call`, 
          clientData
        );
        
        if (response.data.error) {
          throw new Error(response.data.error);
        }
        
        statusMessage.value = labelsState.value['request_success'] || 'Request sent successfully. We will call you shortly.';
        hasError.value = false;
        
        setTimeout(() => {
          showForm.value = false;
          statusMessage.value = '';
          resetForm();
        }, 3000);
        
      } catch (error) {
        if (error.response && error.response.status === 503) {
          statusMessage.value = labelsState.value['no_agents_available_contact'] || 
            'No agents are currently available. We will contact you via WhatsApp soon.';
        } else {
          statusMessage.value = (labelsState.value['error'] || 'Error') + ': ' + 
            (error.message || labelsState.value['general_error'] || 'An error occurred while processing your request');
        }
        hasError.value = true;
      } finally {
        isSubmitting.value = false;
      }
    };
    
    const resetForm = () => {
      formData.value = {
        name: '',
        email: '',
        phone: ''
      };
    };
    
    return {
      showForm,
      formData,
      countries,
      countryCode,
      statusMessage,
      isSubmitting,
      hasError,
      labelsState,
      submitForm,
      resetForm
    };
  }
};
</script>

<style scoped>
.twilio-call-link {
   display: inline-block;
   width: 100%;
   color: #ffffff;
 }
 .status-message {
   color: #e80822;
 }
.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(5px); 
}

.modal-container {
  background-color: #111827;
  padding: 20px;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
}
.modal-title {
  background-color: #111827;
  text-decoration-color: #ffffff;
  color: #ffffff;

}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
}

.form-group input {
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
}

.button-group {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}
.phone-input-container {
  display: flex;
  align-items: center;
  width: 100%;
}

.country-code-select {
  min-width: 110px;
  min-height: 47px;
  border-color: #ffffff;
}

.phone-number-input {
  flex: 1;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
@media (max-width: 480px) {
  .button-group {
    flex-direction: column;
    gap: 10px;
  }
  
  .button-group button {
    width: 100%;
  }
}
</style>